import React, { useEffect, useState } from 'react';
import { CCard, CCardBody, CCardHeader, CCardTitle, CFormGroup } from '@coreui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import _t from 'counterpart';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import { handleFormError } from '../../helpers';
import toast from 'react-hot-toast';
import { fetchRemoveCreditBelowBalance, updateRemoveCreditBelowBalance } from '../../services/BackendService';
import { CSwitch } from '@coreui/react';

const QUERY_KEY = 'remove-credit-below-balance';

const CreditRemovalSetting = () => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [originalValue, setOriginalValue] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const settingQuery = useQuery(QUERY_KEY, fetchRemoveCreditBelowBalance, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const updateMutation = useMutation(() => updateRemoveCreditBelowBalance(enabled), {
    onSuccess: async () => {
      toast.success(_t('credit-removal-setting.successfully-updated'));
      await queryClient.invalidateQueries([QUERY_KEY]);
      setOriginalValue(enabled);
    },
    onError: async (e: any) => {
      handleFormError(e);
    },
  });

  useEffect(() => {
    if (settingQuery.data !== undefined) {
      const isEnabled = Boolean(settingQuery.data.removeCreditWhenBalanceBelowCredit);
      setEnabled(isEnabled);
      setOriginalValue(isEnabled);
    }
  }, [settingQuery.data]);

  const hasChanges = enabled !== originalValue;

  if (settingQuery.isLoading) {
    return (
      <CCard className="mt-4">
        <CCardHeader>
          <CCardTitle>{_t('credit-removal-setting.title')}</CCardTitle>
          <p className="mb-0 mt-1 small">{_t('credit-removal-setting.description')}</p>
        </CCardHeader>
        <CCardBody>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </CCardBody>
      </CCard>
    );
  }

  return (
    <CCard className="mt-4">
      <CCardHeader>
        <CCardTitle>{_t('credit-removal-setting.title')}</CCardTitle>
        <p className="mb-0 mt-1 small">{_t('credit-removal-setting.description')}</p>
      </CCardHeader>
      <CCardBody>
        <CFormGroup>
          <div className="mt-2">
            <CSwitch
              color="primary"
              checked={enabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEnabled(e.target.checked)}
              disabled={settingQuery.isLoading || updateMutation.isLoading}
            />
            <span className="ml-2">
              {enabled
                ? _t('credit-removal-setting.enabled')
                : _t('credit-removal-setting.disabled')}
            </span>
          </div>
          <small className="text-muted mt-2 d-block">{_t('credit-removal-setting.help-text')}</small>
          <small className="text-warning mt-2 d-block">{_t('credit-removal-setting.enable-warning')}</small>
        </CFormGroup>

        <ButtonWithLoader
          isLoading={updateMutation.isLoading}
          onClick={() => updateMutation.mutate()}
          buttonColor="primary"
          spinnerColor="secondary"
          title={_t('action.save')}
          className="btn-md"
          showText={false}
          disabled={!hasChanges}
        />
      </CCardBody>
    </CCard>
  );
};

export default CreditRemovalSetting;
